import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";

const POWDER = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/5c97f9724bfdaad482043e8f51261955fa7fece6ffe9ee3b63347e7b3c7de85d.png";

export default function FreeGuide() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [totalSubscribers, setTotalSubscribers] = useState(null);

  useEffect(() => {
    api.get("/free-guide/stats").then((r) => setTotalSubscribers(r.data.total_subscribers)).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/free-guide/subscribe", {
        email: email.trim(),
        name: name.trim(),
        source: "free-guide-page",
      });
      setSuccess(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="free-guide-page"
      className="relative min-h-[80vh]"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(249,247,243,0.9), rgba(249,247,243,0.98)), url(${POWDER})`,
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
      }}
    >
      <SEO
        title="The Free Makeup Brush Guide — Printable PDF"
        description="A quiet, printable PDF guide to the 12 most-used makeup brushes. Free with your email — no spam, just one good read with morning coffee."
        path="/free-guide"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <div className="overline mb-4">Volume 01 · Free Download</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] text-stone-900 tracking-tight">
            The BrushBasics
            <br />
            <em className="italic text-stone-700">Pocket Guide.</em>
          </h1>

          <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-stone-600 font-light">
            A 32-page magazine-style PDF: every brush in the BrushBasics catalog, the technique that makes each one
            useful, and the care that keeps them going. Yours, free.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            <div className="border-t border-[#E5E0D8] pt-4">
              <div className="font-serif-display text-3xl text-stone-900">30</div>
              <div className="overline mt-1">Brushes</div>
            </div>
            <div className="border-t border-[#E5E0D8] pt-4">
              <div className="font-serif-display text-3xl text-stone-900">32</div>
              <div className="overline mt-1">Pages</div>
            </div>
            <div className="border-t border-[#E5E0D8] pt-4">
              <div className="font-serif-display text-3xl text-stone-900">$0</div>
              <div className="overline mt-1">Always</div>
            </div>
          </div>

          {totalSubscribers !== null && totalSubscribers > 0 && (
            <p className="mt-8 text-sm text-stone-500 italic" data-testid="subscriber-count">
              Joined by {totalSubscribers.toLocaleString()}+ makeup lovers.
            </p>
          )}
        </div>

        <div className="lg:col-span-5">
          <div className="bg-white border border-[#E5E0D8] p-8 lg:p-10">
            {success ? (
              <div data-testid="free-guide-success">
                <div className="overline mb-3">
                  {success.already_subscribed ? "Welcome Back" : "Welcome"}
                </div>
                <h2 className="font-serif-display text-3xl text-stone-900 font-light leading-tight">
                  Your guide is ready.
                </h2>
                <p className="mt-4 text-sm text-stone-600 leading-relaxed">
                  {success.already_subscribed
                    ? "You're already on our list — here's your guide again."
                    : "Thank you for subscribing. Tap below to download — it'll open in a new tab."}
                </p>
                <a
                  data-testid="guide-download-link"
                  href={success.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-block text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
                >
                  Download the Pocket Guide (PDF)
                </a>
                <p className="mt-4 text-xs text-stone-500">
                  Trouble downloading? <a className="underline" href={success.download_url} target="_blank" rel="noopener noreferrer">Right-click here</a> and save as.
                </p>
                <div className="mt-10 border-t border-[#E5E0D8] pt-6">
                  <div className="overline mb-3">Keep exploring</div>
                  <div className="flex flex-wrap gap-3">
                    <Link to="/catalog" className="text-xs uppercase tracking-[0.22em] text-[#8C6A5D] hover:text-[#70544A]">
                      The Catalog →
                    </Link>
                    <Link to="/quiz" className="text-xs uppercase tracking-[0.22em] text-[#8C6A5D] hover:text-[#70544A]">
                      Brush Finder →
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="free-guide-form">
                <div className="overline mb-3">Send it to me</div>
                <h2 className="font-serif-display text-3xl text-stone-900 font-light leading-tight">
                  Enter your email.
                </h2>
                <p className="mt-3 text-sm text-stone-500 leading-relaxed">
                  We'll save your spot and send you future editorial drops. No noise — promise.
                </p>

                <div className="mt-7 space-y-4">
                  <div>
                    <label htmlFor="fg-name" className="overline block mb-2">Name (optional)</label>
                    <input
                      id="fg-name"
                      data-testid="free-guide-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
                      placeholder="Mia"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label htmlFor="fg-email" className="overline block mb-2">Email</label>
                    <input
                      id="fg-email"
                      data-testid="free-guide-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3"
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </div>
                </div>

                {error && (
                  <p data-testid="free-guide-error" className="mt-4 text-sm text-red-700">{error}</p>
                )}

                <button
                  data-testid="free-guide-submit"
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className="mt-7 w-full text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors"
                >
                  {submitting ? "Preparing your guide…" : "Get the Free Guide"}
                </button>

                <p className="mt-5 text-xs text-stone-500 leading-relaxed">
                  By submitting, you agree to receive occasional emails from BrushBasics. You can unsubscribe at any time.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
